/**
 * フィールド生成の GPU ワーカー（EC2。Issue #741）の起動状況と、起動・停止（ダッシュボード）。
 *
 * - EC2 の状態は /wake の field_gen_status（Lambda synchro-ec2wake。インスタンスは Name タグで引く）。
 *   **止める ＝ インスタンスごと消す**、**起動 ＝ 素の AMI から作り、起動スクリプトが導入する（AMI は焼かない。Issue #885。使えるまで 10 分ほど）**。台は常に 1 台
 *   （何度起動しても 1 台。動いていれば止め忘れの予約を延ばすだけ）
 * - ワーカーは field_gen のジョブを 1 本ずつ回し、ジョブが 10 分無ければ自分で止まる（消える）。止め忘れの予約は 6 時間
 * - キューの field_gen（待ち・処理中と進み具合）は GetExportRequests。処理中に止めると、そのジョブは次に起こしたときにやり直す
 * - 起動は費用がかかる（T4 ＝ g4dn.xlarge 約 0.7 ドル/時、A10G ＝ g5.xlarge 約 1.5 ドル/時）ので確かめてから
 * - 状態が変わっている途中（pending / stopping）は 3 秒ごと、それ以外は 15 秒ごとに見直す
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../api/api';
import { stopText } from './LiveServerPanel';

const LABELS = {
  running: ['動いています', '#9be7a1'],
  pending: ['起動中…（ジョブを取り始めるまで数分）', '#f0c36b'],
  stopping: ['停止中…（インスタンスごと消しています）', '#f0c36b'],
  stopped: ['止まっています（起動すると作ります。使えるまで 10 分ほど）', '#8a93a8'],
};
/** 起こすときの大きさ（Lambda の FIELD_GEN_SIZES と同じ鍵） */
export const GPU_SIZES = { t4: 'T4（g4dn.xlarge・約 0.7 ドル/時）', a10g: 'A10G（g5.xlarge・約 1.5 ドル/時）' };

/** キューの field_gen → {pending: 本数, processing: [ジョブ]} */
export function fieldGenQueue(requests) {
  const jobs = (Array.isArray(requests) ? requests : []).filter((r) => r.kind === 'field_gen');
  return {
    pending: jobs.filter((r) => r.status === 'pending').length,
    processing: jobs.filter((r) => r.status === 'processing'),
  };
}

export function queueText(queue) {
  if (!queue) return null;
  const parts = [`待ち ${queue.pending} 本`];
  queue.processing.forEach((r) => parts.push(`処理中: 試合 ${r.game_id}${r.progress ? `（${r.progress}）` : ''}`));
  return parts.join('・');
}

export default function FieldGenWorkerPanel() {
  const [state, setState] = useState(undefined);
  const [stopAt, setStopAt] = useState(null);
  const [runningSize, setRunningSize] = useState(null);
  const [size, setSize] = useState('t4');
  const [queue, setQueue] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const stateRef = useRef(state);
  stateRef.current = state;

  const refresh = useCallback(async () => {
    try {
      const r = await api.liveServer('field_gen_status');
      setState(r.field_gen_state);
      setStopAt(r.scheduled_stop ?? null);
      setRunningSize(r.size ?? null);
      setError(null);
    } catch (e) {
      setError('状態を取れません');
    }
    try {
      setQueue(fieldGenQueue(await api.getExportRequests({ kind: 'field_gen' })));
    } catch (e) {
      setQueue(null);      // 本番が寝ているときなど。台の状態だけ出す
    }
  }, []);

  useEffect(() => {
    let alive = true;
    let timer;
    const loop = async () => {
      await refresh();
      if (!alive) return;
      const s = stateRef.current;
      timer = setTimeout(loop, s === 'pending' || s === 'stopping' ? 3000 : 15000);
    };
    loop();
    return () => { alive = false; clearTimeout(timer); };
  }, [refresh]);

  const act = async (action) => {
    if (action === 'field_gen_start') {
      const n = queue ? `待ちのジョブは ${queue.pending} 本です。` : '';
      if (!window.confirm(`GPU ワーカー（${GPU_SIZES[size]}）を起動しますか？\n${n}ジョブが 10 分無くなると自分で止まります。`)) return;
    }
    if (action === 'field_gen_stop') {
      const doing = queue?.processing?.length ? '処理中のジョブは、次に起動したときにやり直しになります。\n' : '';
      if (!window.confirm(`${doing}GPU ワーカーを止めますか？`)) return;
    }
    setBusy(true);
    try {
      const r = await api.liveServer(action, action === 'field_gen_start' ? { size } : {});
      setState(r.field_gen_state);
      if ('size' in r) setRunningSize(r.size ?? null);
      if ('scheduled_stop' in r) setStopAt(r.scheduled_stop ?? null);
      setError(null);
    } catch (e) {
      setError('操作できませんでした');
    } finally {
      setBusy(false);
    }
  };

  const [label, color] = state === undefined ? ['確認中…', '#8a93a8'] : LABELS[state] ?? [state, '#8a93a8'];
  const q = queueText(queue);
  return (
    <div className="card" data-testid="field-gen-worker-panel" style={{ marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <strong className="card-title" style={{ margin: 0 }}>フィールド生成の GPU ワーカー</strong>
        <span data-testid="field-gen-state" style={{ color, fontWeight: 700 }}>● {label}</span>
        {(state === 'running' || state === 'pending') && runningSize ? (
          <span data-testid="field-gen-size" style={{ color: '#b8c0d4', fontSize: 12 }}>{GPU_SIZES[runningSize] ?? runningSize}</span>
        ) : null}
        <span style={{ flex: 1 }} />
        {state === 'stopped' ? (
          <>
            <select value={size} onChange={(e) => setSize(e.target.value)} disabled={busy} data-testid="field-gen-size-select">
              {Object.entries(GPU_SIZES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            <button type="button" className="btn" disabled={busy} onClick={() => act('field_gen_start')} data-testid="field-gen-start">起動</button>
          </>
        ) : null}
        {state === 'running' || state === 'pending' ? (
          <button type="button" className="btn" disabled={busy} onClick={() => act('field_gen_stop')} data-testid="field-gen-stop">停止</button>
        ) : null}
        <button type="button" className="btn" disabled={busy} onClick={refresh}>更新</button>
      </div>
      <div style={{ marginTop: 6, fontSize: 12, color: '#b8c0d4', display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        {q ? <span data-testid="field-gen-queue">キュー: {q}</span> : null}
        {state === 'running' || state === 'pending' ? (
          <span data-testid="field-gen-stop-at">止め忘れの予約: {stopText(stopAt) ?? 'なし'}（ジョブが 10 分無ければ自分で止まります）</span>
        ) : null}
      </div>
      {error ? <div style={{ marginTop: 6, color: '#ff8a8a', fontSize: 12 }}>{error}</div> : null}
    </div>
  );
}
